// Spiff Week strip — the shared top of home (Ship 1, Aug 25, 2026).
//
// Greg: "we recognize the previous week's spiffs on Mondays and Shannon sends
// $ Tuesday… as people reach the spiffs, their names show so everyone can see."
// Anatomy: (1) Monday-morning recognition banner for LAST week's winners —
// visible Monday 00:00→12:00 ET only, then it retires; winners live on in the
// weekly history. (2) The live race: the 4 program tiles with names as people
// reach them (activity is public by design; dollars stay private-to-self
// elsewhere). (3) Weekly history from the spiffs TABLE (the awarded truth)
// with paid/pending chips off paid_at. (4) This week's goals with a
// Mine|Company toggle. (5) 1-on-1 plan locks for the week.
//
// Oct 19, 2026: the 5-Star Review SPIFF is recognized here with the rest —
// a ⭐ on each rep in the Monday banner, a fifth tile in the live race naming
// this week's reviewers, and a ⭐ in the weekly history. It pays the review's
// commission point, so its dollars are private: the star is public, the amount
// shows only to the rep (and the RPC / RLS hide other reps' amounts anyway).
// It is per review, not a race, so the tile never "closes".
//
// All spiff math goes through lib/weeklySpiffs — the single source of truth;
// no thresholds are re-implemented here. crew_daily_points is ET-bucketed and
// all week keys are ET Mondays. On phones the strip collapses to one line
// (the Today hero owns the first screenful); tap expands it.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import {
  addDaysISO,
  computeSpiffSummary,
  FIVE_STAR_REVIEW_SPIFF,
  fmtReviewStars,
  mondayOf,
  reviewCountsInWeek,
  reviewPayWeek,
  SPIFF_PROGRAM,
  todayDowET,
  weekRangeLabel,
  windowClosed,
  type ReviewCreditRow,
  type SpiffKey,
  type SpiffStat,
  type SpiffSummary,
} from "../../lib/weeklySpiffs";
import {
  fetchReviewCredits,
  fetchSpiffAudit,
  fetchWallStats,
  fetchWeeklyGoalsMap,
  fetchWeeklySpiffData,
  type GoalMetricId,
  type SpiffAuditRow,
  type WeeklyGoalsMap,
} from "../crew/crewQueries";
import { etDay, type PersonalFunnel } from "../../lib/businessSnapshot";
import { logCardClick } from "../../lib/uiEvents";

const fmtHalf = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
const firstName = (full: string | null | undefined) => (full ?? "—").split(" ")[0];
const fmtUSD0 = (n: number) => `$${Math.round(n).toLocaleString()}`;
const fmtUSD2 = (n: number) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const counterFor = (s: SpiffStat, k: SpiffKey): number =>
  k === "monday" ? s.claimsMon : k === "tuesday" ? s.apptsTue : k === "hump" ? s.claimsWed : s.perfectTotal;

function etHourNow(): number {
  return Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      hour: "numeric",
      hour12: false,
    }).format(new Date()),
  );
}

const GOAL_METRICS: Array<{ key: GoalMetricId; label: string }> = [
  { key: "apps", label: "Apps" },
  { key: "claims", label: "Claims" },
  { key: "builds_scheduled", label: "Signed IA" },
  { key: "builds", label: "Builds" },
];

interface HistoryWeek {
  monday: string;
  total: number;
  parts: string;
  /** False when the viewer can see no SPIFF rows for the week (a review-only week). */
  hasRows: boolean;
  allPaid: boolean;
}

export default function SpiffWeekStrip({
  member,
  summary,
  weekMonday,
  myFunnel,
  myCommitLocked,
  locksView,
  rosterRows,
}: {
  member: { id: string; full_name: string | null };
  /** Current-week spiff summary, computed once in HomeRedesign. */
  summary: SpiffSummary | null;
  /** ET Monday of the current week. */
  weekMonday: string;
  /** My this-week funnel from the business snapshot (Mine goals actuals). */
  myFunnel: PersonalFunnel | null;
  /** Whether my own weekly plan is locked (null while loading). */
  myCommitLocked: boolean | null;
  /**
   * How the 1-on-1 locks card renders. "list" = manager tier whose RLS can
   * actually read other reps' weekly_commitments (canvas_can_manage roles +
   * super-users); "self" = field rep (own status only, no query — an RM's
   * RLS-filtered list read would silently look empty, the Jul-14 cross-RLS
   * blindness class); "none" = roles outside the ritual (finance/office/…).
   */
  locksView: "self" | "list" | "none";
  /** Active roster (id/full_name/role) for lock names + eligible counts. */
  rosterRows: Array<{ id: string; full_name: string; role: string }>;
}) {
  const dowET = todayDowET();
  const bannerOpen = dowET === 1 && etHourNow() < 12;
  const lastMonday = addDaysISO(weekMonday, -7);

  const [lastWeek, setLastWeek] = useState<SpiffSummary | null>(null);
  const [goalsMap, setGoalsMap] = useState<WeeklyGoalsMap | null>(null);
  const [companyActuals, setCompanyActuals] = useState<Record<GoalMetricId, number> | null>(null);
  const [history, setHistory] = useState<HistoryWeek[]>([]);
  const [lockedIds, setLockedIds] = useState<Set<string> | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [goalScope, setGoalScope] = useState<"mine" | "company" | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // The history's ⭐ come from the review RPC (every review, whoever's pay
      // the viewer may see); its dollars come from the visible spiffs rows.
      // Reviews run Monday noon to Monday noon, so the last history week reaches
      // into this Monday morning (reviewPayWeek sorts them out below).
      const historyFrom = addDaysISO(weekMonday, -28);
      const reviewFrom =
        historyFrom > FIVE_STAR_REVIEW_SPIFF.startsWeekOf ? historyFrom : FIVE_STAR_REVIEW_SPIFF.startsWeekOf;
      const reviewTo = weekMonday;
      const [goalsRes, wallRes, auditRes, locksRes, lastRes, reviewRes] = await Promise.allSettled([
        fetchWeeklyGoalsMap(weekMonday),
        fetchWallStats("this_week", { buildsOnCalendar: true }),
        fetchSpiffAudit(historyFrom, weekMonday),
        // Only manager-tier viewers query the list — anyone else's RLS-filtered
        // read would silently return "nobody locked" (cross-RLS blindness).
        locksView === "list"
          ? supabase
              .from("weekly_commitments")
              .select("user_id, locked_at")
              .eq("week_start", weekMonday)
              .not("locked_at", "is", null)
          : Promise.resolve(null),
        bannerOpen ? fetchWeeklySpiffData(lastMonday) : Promise.resolve(null),
        reviewFrom <= reviewTo ? fetchReviewCredits(reviewFrom, reviewTo) : Promise.resolve([] as ReviewCreditRow[]),
      ]);
      if (cancelled) return;

      if (goalsRes.status === "fulfilled") setGoalsMap(goalsRes.value);
      if (wallRes.status === "fulfilled") {
        const totals: Record<GoalMetricId, number> = { apps: 0, claims: 0, builds_scheduled: 0, builds: 0 };
        for (const w of Object.values(wallRes.value.stats)) {
          totals.apps += Number(w.apps ?? 0);
          totals.claims += Number(w.claims ?? 0);
          totals.builds_scheduled += Number(w.builds_scheduled ?? 0);
          totals.builds += Number(w.builds ?? 0);
        }
        setCompanyActuals(totals);
      }
      if (auditRes.status === "fulfilled") {
        const salesNames = new Map(
          rosterRows
            .filter((r) => r.role === "restore_manager" || r.role === "team_lead")
            .map((r) => [r.id, r.full_name] as const),
        );
        // Only finished weeks: a review from this Monday afternoon belongs to this week.
        const historyCredits =
          reviewRes.status === "fulfilled"
            ? reviewRes.value.filter((c) => {
                const wk = reviewPayWeek(c.verified_at);
                return wk >= historyFrom && wk < weekMonday && reviewCountsInWeek(c, wk);
              })
            : null;
        setHistory(groupHistory(auditRes.value, historyCredits, salesNames));
      }
      if (locksRes.status === "fulfilled" && locksRes.value && !locksRes.value.error) {
        setLockedIds(new Set(((locksRes.value.data as Array<{ user_id: string }>) ?? []).map((r) => r.user_id)));
      }
      if (lastRes.status === "fulfilled" && lastRes.value) {
        setLastWeek(
          computeSpiffSummary(lastRes.value.activity, lastRes.value.members, lastMonday, lastRes.value.reviews),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member.id, weekMonday]);

  // Goals scope: remembered per user; smart default = Mine when personal
  // targets exist, else Company.
  const scopeStorageKey = `bp_goal_scope_${member.id}`;
  useEffect(() => {
    if (goalScope !== null || goalsMap === null) return;
    try {
      const raw = localStorage.getItem(scopeStorageKey);
      if (raw === "mine" || raw === "company") {
        setGoalScope(raw);
        return;
      }
    } catch {
      /* fall through to smart default */
    }
    const mine = goalsMap[member.id];
    setGoalScope(mine && Object.keys(mine).length > 0 ? "mine" : "company");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalsMap, goalScope]);

  const pickGoalScope = (s: "mine" | "company") => {
    setGoalScope(s);
    logCardClick("spiffstrip_goalscope");
    try {
      localStorage.setItem(scopeStorageKey, s);
    } catch {
      /* best-effort */
    }
  };

  // Everyone with a SPIFF or a verified 5★ review last week is recognized.
  const winners = useMemo(
    () => (lastWeek ? lastWeek.list.filter((s) => s.totalEarned > 0 || s.reviews > 0) : []),
    [lastWeek],
  );
  const myLastWeekReview = lastWeek?.perRM[member.id]?.reviewEarned ?? 0;

  const salesReps = rosterRows.filter((r) => r.role === "restore_manager" || r.role === "team_lead");
  const lockedNames = lockedIds
    ? salesReps.filter((r) => lockedIds.has(r.id)).map((r) => firstName(r.full_name))
    : [];
  const unlockedCount = lockedIds
    ? Math.max(0, salesReps.length - lockedNames.length)
    : null;

  const mineTargets = goalsMap?.[member.id];
  const companyTargets = goalsMap?.["company"];
  const scope = goalScope ?? "company";
  const targets = scope === "mine" ? mineTargets : companyTargets;
  const actualFor = (k: GoalMetricId): number => {
    if (scope === "company") return companyActuals?.[k] ?? 0;
    if (!myFunnel) return 0;
    return k === "apps"
      ? myFunnel.apps
      : k === "claims"
        ? myFunnel.claims
        : k === "builds_scheduled"
          ? myFunnel.signedIA
          : myFunnel.builds;
  };

  const mobileSummary = bannerOpen && winners.length > 0
    ? `🏆 Last wk: ${firstName(winners[0].name)} ${
        winners[0].totalEarned > 0 ? fmtUSD0(winners[0].totalEarned) : fmtReviewStars(winners[0].reviews)
      }${winners.length > 1 ? ` +${winners.length - 1} more` : ""}`
    : summary && summary.totalPaidOut > 0
      ? `${fmtUSD0(summary.totalPaidOut)} earned so far this week`
      : "the race is on";

  return (
    <div className="rounded-2xl border border-orange-200 bg-gradient-to-b from-orange-50/70 to-white p-3.5 lg:p-4 mb-5">
      {/* Mobile: one-line summary, tap to expand (the Today hero owns screen one) */}
      <button
        type="button"
        onClick={() => {
          setExpanded((v) => !v);
          logCardClick("spiffstrip_expand");
        }}
        className="lg:hidden w-full flex items-center gap-2 text-left"
      >
        <span className="text-[12px] font-extrabold text-bardin-ink">🎉 Spiff week</span>
        <span className="text-[11px] font-semibold text-slate-500 truncate flex-1">{mobileSummary}</span>
        <span className="text-bardin-orange text-xs font-bold">{expanded ? "▴" : "▾"}</span>
      </button>

      <div className={`${expanded ? "block mt-3" : "hidden"} lg:block`}>
        <div className="flex items-baseline gap-2.5 flex-wrap">
          <span className="font-extrabold text-[15px] text-bardin-ink">
            🎉 Week of {weekRangeLabel(weekMonday)}
          </span>
          <span className="text-[11px] text-slate-500 font-medium">
            Spiffs recognized Mondays · Shannon sends money Tuesdays
          </span>
        </div>

        {/* Monday-morning recognition — auto-retires at noon ET */}
        {bannerOpen && winners.length > 0 && (
          <div className="mt-2.5 rounded-xl border border-orange-200 bg-white p-3">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="text-[10px] font-extrabold uppercase tracking-widest text-bardin-orange">
                🏆 Recognized this morning — week of {weekRangeLabel(lastMonday)}
              </span>
              <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                💵 {fmtUSD0(lastWeek?.totalPaidOut ?? 0)}
                {(lastWeek?.reviewReps ?? 0) > 0 ? " + 5★ review SPIFFs" : ""} + ASM override · Shannon pays tomorrow
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {winners.map((w) => {
                const sweep = SPIFF_PROGRAM.every((p) => w.earned[p.key]);
                return (
                  <span
                    key={w.id}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${
                      sweep ? "bg-bardin-orange text-white" : "bg-slate-100 text-bardin-ink"
                    }`}
                  >
                    {w.name}
                    {w.reviews > 0 && (
                      <span title={`${FIVE_STAR_REVIEW_SPIFF.name} SPIFF`}> {fmtReviewStars(w.reviews)}</span>
                    )}
                    {sweep ? " — swept all four " : " "}
                    {w.totalEarned > 0 && (
                      <span className={sweep ? "text-orange-100" : "text-emerald-700"}>
                        {fmtUSD0(w.totalEarned)}
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
            {myLastWeekReview > 0 && (
              <div className="mt-2 text-[10.5px] font-semibold text-emerald-800">
                ⭐ Your 5-Star Review SPIFF: {fmtUSD2(myLastWeekReview)} — your 5★ commission point, paid tomorrow
                with the SPIFFs.
              </div>
            )}
          </div>
        )}

        {/* The live race — names show as people reach each spiff */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mt-2.5">
          {SPIFF_PROGRAM.map((p) => {
            const closed = windowClosed(p.key, dowET);
            const stats = summary?.list ?? [];
            const earned = stats.filter((s) => s.earned[p.key]);
            const leaders = closed
              ? []
              : stats
                  .filter((s) => !s.earned[p.key] && counterFor(s, p.key) > 0)
                  .sort((a, b) => counterFor(b, p.key) - counterFor(a, p.key))
                  .slice(0, 2);
            return (
              <div
                key={p.key}
                className={`rounded-xl border bg-white p-2.5 ${closed && earned.length === 0 ? "opacity-60" : ""} border-slate-200`}
              >
                <div className="flex items-baseline justify-between gap-1">
                  <span className="text-[11px] font-extrabold text-bardin-ink">{p.name}</span>
                  <span className="text-[11px] font-extrabold text-bardin-orange">
                    ${p.prizeCents / 100}
                  </span>
                </div>
                <div className="text-[9.5px] text-slate-400 font-medium mb-1.5">{p.shortRule}</div>
                <div className="flex flex-wrap gap-1">
                  {earned.map((s) => (
                    <span
                      key={s.id}
                      className="px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold"
                    >
                      {firstName(s.name)} ✓
                    </span>
                  ))}
                  {leaders.map((s) => (
                    <span
                      key={s.id}
                      className="px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold"
                    >
                      {firstName(s.name)} {fmtHalf(counterFor(s, p.key))}/{fmtHalf(p.target)}
                    </span>
                  ))}
                  {earned.length === 0 && leaders.length === 0 && (
                    <span className="text-[10px] text-slate-400 font-medium">
                      {closed ? "nobody this week" : summary ? "wide open" : "…"}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {/* 5-Star Review SPIFF — per review, Monday noon to Monday noon, so it never closes */}
          <ReviewTile summary={summary} meId={member.id} />
        </div>

        {/* Goals (Mine|Company) + 1-on-1 locks */}
        <div className={`lg:grid lg:gap-2.5 mt-2.5 space-y-2.5 lg:space-y-0 ${locksView === "none" ? "lg:grid-cols-1" : "lg:grid-cols-3"}`}>
          <div className={`${locksView === "none" ? "" : "lg:col-span-2"} rounded-xl border border-slate-200 bg-white p-2.5`}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">
                Goals · this week
              </span>
              <div className="ml-auto flex rounded-full border border-slate-200 overflow-hidden text-[10px] font-bold">
                {(["mine", "company"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => pickGoalScope(s)}
                    className={`px-2.5 py-0.5 ${scope === s ? "bg-bardin-ink text-white" : "text-slate-400"}`}
                  >
                    {s === "mine" ? "Mine" : "Company"}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {GOAL_METRICS.map((m) => {
                const target = targets?.[m.key];
                const actual = actualFor(m.key);
                const pct = target && target > 0 ? Math.min(100, (actual / target) * 100) : 0;
                const hit = target != null && target > 0 && actual >= target;
                return (
                  <div key={m.key}>
                    <div
                      className={`text-sm font-extrabold leading-none ${hit ? "text-emerald-600" : "text-bardin-ink"}`}
                    >
                      {fmtHalf(actual)}
                      <span className="text-[10px] text-slate-400 font-bold">
                        {target && target > 0 ? ` / ${fmtHalf(target)}` : " / —"}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden my-1">
                      <div className="h-full rounded-full bg-bardin-orange" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-[9px] uppercase tracking-wide font-bold text-slate-400">{m.label}</div>
                  </div>
                );
              })}
            </div>
            {scope === "mine" && (!mineTargets || Object.keys(mineTargets).length === 0) && (
              <div className="mt-1.5 text-[10px] font-semibold text-amber-700 bg-amber-50 rounded-md px-2 py-1">
                No personal targets set for your week yet — they're set at your Monday 1-on-1.
              </div>
            )}
          </div>

          {locksView !== "none" && (
            <div className="rounded-xl border border-slate-200 bg-white p-2.5">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mb-1.5">
                1-on-1 plans · this week
              </div>
              {locksView === "self" ? (
                myCommitLocked === null ? (
                  <div className="text-[11px] text-slate-400">…</div>
                ) : myCommitLocked ? (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold">
                    Your plan ✓ locked
                  </span>
                ) : (
                  <div className="text-[11px] font-semibold text-amber-700">
                    Your week plan isn't locked yet — lock it at your 1-on-1.
                  </div>
                )
              ) : lockedIds === null ? (
                <div className="text-[11px] text-slate-400">…</div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-1">
                    {lockedNames.length === 0 ? (
                      <span className="text-[11px] text-slate-400">No plans locked yet.</span>
                    ) : (
                      lockedNames.map((n) => (
                        <span
                          key={n}
                          className="px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold"
                        >
                          {n} ✓
                        </span>
                      ))
                    )}
                  </div>
                  {unlockedCount !== null && unlockedCount > 0 && (
                    <div className="text-[10px] text-slate-500 font-semibold mt-1.5">
                      {unlockedCount} sales rep{unlockedCount === 1 ? "" : "s"} still to lock.
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Weekly history — from the spiffs table, the awarded truth */}
        {history.length > 0 && (
          <details className="mt-2.5" onToggle={() => logCardClick("spiffstrip_history")}>
            <summary className="text-[11px] font-bold text-bardin-orange cursor-pointer select-none">
              📜 Weekly history
            </summary>
            <div className="mt-1.5 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white px-3">
              {history.map((h) => (
                <div key={h.monday} className="flex items-baseline gap-2 py-1.5 flex-wrap text-[11px]">
                  <span className="font-extrabold text-bardin-ink whitespace-nowrap">
                    Week of {weekRangeLabel(h.monday)}
                  </span>
                  <span className="text-slate-500 flex-1 min-w-[160px]">{h.parts}</span>
                  {h.hasRows && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      h.allPaid
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {h.allPaid ? "✓ paid" : dowET === 2 ? "💵 going out today" : "pays Tue"}
                  </span>
                  )}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

/** The 5-Star Review tile: everyone credited with a verified review this week.
 *  The ⭐ are public; the dollar line is the viewer's own and nobody else's. */
function ReviewTile({ summary, meId }: { summary: SpiffSummary | null; meId: string }) {
  const reviewers = (summary?.list ?? [])
    .filter((s) => s.reviews > 0)
    .sort((a, b) => b.reviews - a.reviews || a.name.localeCompare(b.name));
  const me = summary?.perRM[meId];
  const myLine =
    me && me.reviews > 0
      ? me.reviewEarned > 0
        ? `You: ${fmtReviewStars(me.reviews)} · ${fmtUSD2(me.reviewEarned)} on Tuesday's SPIFFs`
        : me.reviewAmountKnown
          ? `You: ${fmtReviewStars(me.reviews)} · recognized (no SPIFF on these jobs)`
          : `You: ${fmtReviewStars(me.reviews)}`
      : null;
  return (
    <div className="col-span-2 lg:col-span-1 rounded-xl border border-slate-200 bg-white p-2.5">
      <div className="flex items-baseline justify-between gap-1">
        <span className="text-[11px] font-extrabold text-bardin-ink">⭐ {FIVE_STAR_REVIEW_SPIFF.name}</span>
        <span className="text-[11px] font-extrabold text-bardin-orange">5★ point</span>
      </div>
      <div className="text-[9.5px] text-slate-400 font-medium mb-1.5">{FIVE_STAR_REVIEW_SPIFF.shortRule}</div>
      <div className="flex flex-wrap gap-1">
        {reviewers.map((s) => (
          <span
            key={s.id}
            className="px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold"
          >
            {firstName(s.name)} {fmtReviewStars(s.reviews)}
          </span>
        ))}
        {reviewers.length === 0 && (
          <span className="text-[10px] text-slate-400 font-medium">
            {!summary ? "…" : summary.reviewsKnown ? "none yet this week" : "couldn't load reviews"}
          </span>
        )}
      </div>
      {myLine && <div className="mt-1.5 text-[10px] font-bold text-emerald-700">{myLine}</div>}
    </div>
  );
}

/** Group audit rows into per-week summaries, newest first. The ⭐ come from
 *  the week's review credits (public recognition, every rep), by pay week —
 *  Monday noon to Monday noon; the dollars are the spiffs rows this viewer can
 *  see. With no credits (read failed), the visible review rows still mark
 *  their reps. */
function groupHistory(
  rows: SpiffAuditRow[],
  credits: ReviewCreditRow[] | null,
  salesNames: Map<string, string>,
): HistoryWeek[] {
  const byWeek = new Map<string, SpiffAuditRow[]>();
  for (const r of rows) {
    const day = etDay(r.claimed_at);
    if (!day) continue;
    const monday = mondayOf(new Date(`${day}T12:00:00Z`));
    const arr = byWeek.get(monday) ?? [];
    arr.push(r);
    byWeek.set(monday, arr);
  }
  // week Monday → first name → review credit
  const starsByWeek = new Map<string, Map<string, number>>();
  for (const c of credits ?? []) {
    const full = salesNames.get(c.user_id);
    if (!full || !c.verified_at) continue;
    const monday = reviewPayWeek(c.verified_at);
    const m = starsByWeek.get(monday) ?? new Map<string, number>();
    const name = firstName(full);
    m.set(name, (m.get(name) ?? 0) + Number(c.share || 0));
    starsByWeek.set(monday, m);
  }
  const out: HistoryWeek[] = [];
  for (const monday of new Set([...byWeek.keys(), ...starsByWeek.keys()])) {
    const weekRows = byWeek.get(monday) ?? [];
    const perPerson = new Map<string, number>();
    const stars = new Map(starsByWeek.get(monday) ?? []);
    let total = 0;
    for (const r of weekRows) {
      const amt = Number(r.amount_cents ?? 0) / 100;
      total += amt;
      const name = firstName(r.recipient_name);
      perPerson.set(name, (perPerson.get(name) ?? 0) + amt);
      if (credits === null && r.name === FIVE_STAR_REVIEW_SPIFF.name && !stars.has(name)) stars.set(name, 1);
    }
    for (const name of stars.keys()) if (!perPerson.has(name)) perPerson.set(name, 0);
    const parts = [...perPerson.entries()]
      .sort((a, b) => b[1] - a[1] || (stars.get(b[0]) ?? 0) - (stars.get(a[0]) ?? 0) || a[0].localeCompare(b[0]))
      .map(([name, amt]) => {
        const credit = stars.get(name) ?? 0;
        const star = credit > 0 ? ` ${credits === null ? "⭐" : fmtReviewStars(credit)}` : "";
        return `${name}${star}${amt > 0 ? ` ${fmtUSD0(amt)}` : ""}`;
      })
      .join(" · ");
    out.push({
      monday,
      total,
      parts,
      hasRows: weekRows.length > 0,
      allPaid: weekRows.length > 0 && weekRows.every((r) => !!r.paid_at),
    });
  }
  return out.sort((a, b) => b.monday.localeCompare(a.monday));
}
